import React, { useEffect, useRef, useState } from 'react';
import { CheckBadgeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../../services/ApiService';
import EditorChrome, { type EditorChromeVariant } from '../../ui/EditorChrome';
import Button from '../../ui/Button';
import { ShopRequestModal } from '../../shops/ShopRequestModal';
import { useToast } from '../../../contexts/ToastContext';
import type { ContributorType, Shop } from '../../../types/buildExpansion';

/**
 * Owner flow to credit a contributor on the build: a shop (searched from the
 * directory), a Bilt member (searched by username), or a plain text credit.
 * Shops and members must accept before the credit appears publicly; text
 * credits are approved immediately since they carry no identity claim.
 */

/** shopContributorRoles() in shop_utils.php */
const SHOP_ROLES = [
  'builder', 'tuner', 'fabricator', 'engine_builder', 'transmission_builder',
  'paint_body', 'wrap', 'suspension_setup', 'alignment', 'photographer',
  'detailer', 'parts_supplier', 'other',
];

/** personContributorRoles() in shop_utils.php (user and credit types) */
const PERSON_ROLES = [
  'owner', 'driver', 'builder', 'tuner', 'fabricator', 'photographer',
  'designer', 'crew_chief', 'previous_owner', 'co_owner', 'other',
];

const ROLE_LABELS: Record<string, string> = {
  builder: 'Builder',
  tuner: 'Tuner',
  fabricator: 'Fabricator',
  engine_builder: 'Engine builder',
  transmission_builder: 'Transmission builder',
  paint_body: 'Paint and body',
  wrap: 'Wrap',
  suspension_setup: 'Suspension setup',
  alignment: 'Alignment',
  photographer: 'Photographer',
  detailer: 'Detailer',
  parts_supplier: 'Parts supplier',
  owner: 'Owner',
  driver: 'Driver',
  designer: 'Designer',
  crew_chief: 'Crew chief',
  previous_owner: 'Previous owner',
  co_owner: 'Co-owner',
  other: 'Other',
};

interface MemberResult {
  id: number;
  username: string;
}

const TYPE_OPTIONS: { value: ContributorType; label: string; help: string }[] = [
  { value: 'shop', label: 'Shop', help: 'A shop from the Bilt directory' },
  { value: 'user', label: 'Bilt member', help: 'Another member, by username' },
  { value: 'credit', label: 'Text credit', help: 'A name only, no account' },
];

export interface ContributorRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  /** Called after the contributor request is created. */
  onCreated?: () => void;
  /** `panel` embeds the form inside Manage build without a nested dialog. */
  variant?: EditorChromeVariant;
}

export const ContributorRequestModal: React.FC<ContributorRequestModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  onCreated,
  variant = 'modal',
}) => {
  const { showToast } = useToast();

  const [contributorType, setContributorType] = useState<ContributorType>('shop');
  const [role, setRole] = useState('builder');
  const [workDescription, setWorkDescription] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [saving, setSaving] = useState(false);

  // Shop search
  const [shopQuery, setShopQuery] = useState('');
  const [shopResults, setShopResults] = useState<Shop[]>([]);
  const [shopSearching, setShopSearching] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [shopRequestOpen, setShopRequestOpen] = useState(false);

  // Member search
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState<MemberResult[]>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberResult | null>(null);

  // Text credit
  const [creditName, setCreditName] = useState('');

  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setContributorType('shop');
    setRole('builder');
    setWorkDescription('');
    setDateFrom('');
    setDateTo('');
    setErrors({});
    setGeneralError('');
    setShopQuery('');
    setShopResults([]);
    setSelectedShop(null);
    setMemberQuery('');
    setMemberResults([]);
    setSelectedMember(null);
    setCreditName('');
  }, [isOpen]);

  // Debounced shop directory search
  useEffect(() => {
    if (!isOpen || contributorType !== 'shop') return;
    const q = shopQuery.trim();
    if (q.length < 2) {
      setShopResults([]);
      setShopSearching(false);
      return;
    }
    setShopSearching(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      apiService
        .searchShops(q)
        .then((shops) => setShopResults(shops.slice(0, 8)))
        .catch(() => setShopResults([]))
        .finally(() => setShopSearching(false));
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [isOpen, contributorType, shopQuery]);

  // Debounced member search via global search (members bucket)
  useEffect(() => {
    if (!isOpen || contributorType !== 'user') return;
    const q = memberQuery.trim();
    if (q.length < 2) {
      setMemberResults([]);
      setMemberSearching(false);
      return;
    }
    setMemberSearching(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      apiService
        .globalSearch(q, 8)
        .then((results) => {
          const members = (results.members ?? [])
            .filter((r) => typeof r.id === 'number')
            .map((r) => ({ id: r.id as number, username: r.title }));
          setMemberResults(members);
        })
        .catch(() => setMemberResults([]))
        .finally(() => setMemberSearching(false));
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [isOpen, contributorType, memberQuery]);

  const roles = contributorType === 'shop' ? SHOP_ROLES : PERSON_ROLES;

  const handleTypeChange = (next: ContributorType) => {
    setContributorType(next);
    setErrors({});
    setGeneralError('');
    const validRoles = next === 'shop' ? SHOP_ROLES : PERSON_ROLES;
    if (!validRoles.includes(role)) setRole(validRoles[0]);
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (contributorType === 'shop' && !selectedShop) {
      next.shop = 'Search for a shop and pick one from the results.';
    }
    if (contributorType === 'user' && !selectedMember) {
      next.member = 'Search for a member and pick one from the results.';
    }
    if (contributorType === 'credit' && creditName.trim() === '') {
      next.credit = 'Enter a name for the credit.';
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      next.date_to = 'End date must be after the start date.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setGeneralError('');
    if (!validate()) return;

    const payload: Record<string, unknown> = {
      vehicle_id: vehicleId,
      contributor_type: contributorType,
      role,
      work_description: workDescription.trim() || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    };
    if (contributorType === 'shop') payload.shop_id = selectedShop!.id;
    if (contributorType === 'user') payload.user_id = selectedMember!.id;
    if (contributorType === 'credit') payload.display_name = creditName.trim();

    setSaving(true);
    try {
      await apiService.contributorAction('create', payload);
      showToast(
        contributorType === 'credit'
          ? 'Credit added to the build.'
          : 'Request sent. It appears once they accept.',
        'success'
      );
      onCreated?.();
      if (variant === 'modal') {
        onClose();
      } else {
        setWorkDescription('');
        setDateFrom('');
        setDateTo('');
        setCreditName('');
        setSelectedShop(null);
        setSelectedMember(null);
        setShopQuery('');
        setMemberQuery('');
        setErrors({});
      }
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Failed to add the contributor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <EditorChrome
      variant={variant}
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Add a contributor"
      description="Credit the shops and people behind this build."
      size="lg"
      closeOnOverlayClick={!saving}
      closeOnEscape={!saving}
      showClose={variant === 'modal'}
      footer={
        <div
          className={`flex gap-2 ${
            variant === 'panel' ? 'flex-col' : 'flex-col-reverse sm:flex-row sm:justify-end'
          }`}
        >
          {variant === 'modal' ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={saving}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            form="contributor-request-form"
            variant="primary"
            loading={saving}
            loadingLabel="Sending…"
            fullWidth={variant === 'panel'}
            className={`min-h-[44px] ${variant === 'panel' ? 'justify-center' : ''}`}
          >
            {contributorType === 'credit' ? 'Add credit' : 'Send request'}
          </Button>
        </div>
      }
    >
      {generalError && (
        <div
          className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {generalError}
        </div>
      )}

      <form id="contributor-request-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        <fieldset>
          <legend className="input-label">
            Contributor type
          </legend>
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-2"
            role="radiogroup"
            aria-label="Contributor type"
          >
            {TYPE_OPTIONS.map((opt) => {
              const active = contributorType === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`rounded-lg border p-3 cursor-pointer min-h-[44px] transition-colors ${
                    active
                      ? 'border-orange-500/60 bg-orange-500/10'
                      : 'border-white/[0.08] bg-[#0a0a0a] hover:border-white/[0.18]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="contributor_type"
                      value={opt.value}
                      checked={active}
                      onChange={() => handleTypeChange(opt.value)}
                      className="checkbox-control"
                      disabled={saving}
                    />
                    <span className="text-sm font-medium text-gray-100">{opt.label}</span>
                  </span>
                  <span className="block text-xs text-gray-500 mt-1">{opt.help}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {(contributorType === 'shop' || contributorType === 'user') && (
          <div
            className="p-3 rounded-lg border border-sky-500/25 bg-sky-500/10 text-sky-200 text-sm"
            role="note"
          >
            They must accept before this appears on the build.
          </div>
        )}

        {contributorType === 'shop' && (
          <div>
            <label htmlFor="contrib-shop-search" className="input-label">
              Shop *
            </label>
            {selectedShop ? (
              <div className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-[#0a0a0a] p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate flex items-center gap-1.5">
                    {selectedShop.name}
                    {selectedShop.is_verified && (
                      <CheckBadgeIcon className="w-4 h-4 text-emerald-400" aria-label="Verified shop" />
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {[selectedShop.city, selectedShop.state].filter(Boolean).join(', ') ||
                      'Location not listed'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedShop(null)}
                  disabled={saving}
                  className="btn-icon min-w-[44px] min-h-[44px] shrink-0 cursor-pointer"
                  aria-label={`Remove selected shop ${selectedShop.name}`}
                >
                  <XMarkIcon className="w-4 h-4" aria-hidden />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  id="contrib-shop-search"
                  value={shopQuery}
                  onChange={(e) => {
                    setShopQuery(e.target.value);
                    if (errors.shop) setErrors((prev) => ({ ...prev, shop: '' }));
                  }}
                  className={`input min-h-[44px] ${errors.shop ? 'border-red-500' : ''}`}
                  placeholder="Search the shop directory"
                  disabled={saving}
                  aria-invalid={Boolean(errors.shop)}
                  aria-describedby={errors.shop ? 'contrib-shop-error' : 'contrib-shop-help'}
                  autoComplete="off"
                />
                {errors.shop ? (
                  <p id="contrib-shop-error" className="mt-1 text-sm text-red-400">
                    {errors.shop}
                  </p>
                ) : (
                  <p id="contrib-shop-help" className="mt-1 text-xs text-gray-500">
                    Type at least 2 characters to search.
                  </p>
                )}
                {shopSearching && (
                  <p className="mt-2 text-sm text-gray-400 flex items-center gap-2" role="status">
                    <span className="spinner-sm" aria-hidden />
                    Searching shops…
                  </p>
                )}
                {!shopSearching && shopQuery.trim().length >= 2 && shopResults.length === 0 && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-gray-400">No approved shops matched that search.</p>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setShopRequestOpen(true)}>
                      Request this shop
                    </Button>
                  </div>
                )}
                {shopResults.length > 0 && (
                  <ul className="mt-2 space-y-1 list-none" aria-label="Shop results">
                    {shopResults.map((shop) => (
                      <li key={shop.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedShop(shop);
                            setShopResults([]);
                            setShopQuery('');
                          }}
                          disabled={saving}
                          className="w-full text-left rounded-lg border border-white/[0.06] bg-[#0c0c0c]/70 hover:border-white/[0.14] transition-colors p-3 min-h-[44px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        >
                          <span className="text-sm font-medium text-gray-100 flex items-center gap-1.5">
                            {shop.name}
                            {shop.is_verified && (
                              <CheckBadgeIcon
                                className="w-4 h-4 text-emerald-400"
                                aria-label="Verified shop"
                              />
                            )}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {[shop.city, shop.state].filter(Boolean).join(', ') ||
                              'Location not listed'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {contributorType === 'user' && (
          <div>
            <label htmlFor="contrib-member-search" className="input-label">
              Member username *
            </label>
            {selectedMember ? (
              <div className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-[#0a0a0a] p-3">
                <p className="text-sm font-medium text-white flex-1 min-w-0 truncate">
                  @{selectedMember.username}
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedMember(null)}
                  disabled={saving}
                  className="btn-icon min-w-[44px] min-h-[44px] shrink-0 cursor-pointer"
                  aria-label={`Remove selected member ${selectedMember.username}`}
                >
                  <XMarkIcon className="w-4 h-4" aria-hidden />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  id="contrib-member-search"
                  value={memberQuery}
                  onChange={(e) => {
                    setMemberQuery(e.target.value);
                    if (errors.member) setErrors((prev) => ({ ...prev, member: '' }));
                  }}
                  className={`input min-h-[44px] ${errors.member ? 'border-red-500' : ''}`}
                  placeholder="Start typing a username"
                  disabled={saving}
                  aria-invalid={Boolean(errors.member)}
                  aria-describedby={errors.member ? 'contrib-member-error' : 'contrib-member-help'}
                  autoComplete="off"
                />
                {errors.member ? (
                  <p id="contrib-member-error" className="mt-1 text-sm text-red-400">
                    {errors.member}
                  </p>
                ) : (
                  <p id="contrib-member-help" className="mt-1 text-xs text-gray-500">
                    Pick the member from the results so the right account is credited.
                  </p>
                )}
                {memberSearching && (
                  <p className="mt-2 text-sm text-gray-400 flex items-center gap-2" role="status">
                    <span className="spinner-sm" aria-hidden />
                    Searching members…
                  </p>
                )}
                {!memberSearching && memberQuery.trim().length >= 2 && memberResults.length === 0 && (
                  <p className="mt-2 text-sm text-gray-400">No members matched that username.</p>
                )}
                {memberResults.length > 0 && (
                  <ul className="mt-2 space-y-1 list-none" aria-label="Member results">
                    {memberResults.map((member) => (
                      <li key={member.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMember(member);
                            setMemberResults([]);
                            setMemberQuery('');
                          }}
                          disabled={saving}
                          className="w-full text-left rounded-lg border border-white/[0.06] bg-[#0c0c0c]/70 hover:border-white/[0.14] transition-colors p-3 min-h-[44px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        >
                          <span className="text-sm font-medium text-gray-100">
                            @{member.username}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {contributorType === 'credit' && (
          <div>
            <label htmlFor="contrib-credit-name" className="input-label">
              Name *
            </label>
            <input
              type="text"
              id="contrib-credit-name"
              value={creditName}
              onChange={(e) => {
                setCreditName(e.target.value);
                if (errors.credit) setErrors((prev) => ({ ...prev, credit: '' }));
              }}
              maxLength={120}
              className={`input min-h-[44px] ${errors.credit ? 'border-red-500' : ''}`}
              placeholder="e.g. Dave at the old shop"
              disabled={saving}
              aria-invalid={Boolean(errors.credit)}
              aria-describedby={errors.credit ? 'contrib-credit-error' : 'contrib-credit-help'}
            />
            {errors.credit ? (
              <p id="contrib-credit-error" className="mt-1 text-sm text-red-400">
                {errors.credit}
              </p>
            ) : (
              <p id="contrib-credit-help" className="mt-1 text-xs text-gray-500">
                Text credits appear immediately and are not verified.
              </p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="contrib-role" className="input-label">
            Role *
          </label>
          <select
            id="contrib-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input min-h-[44px]"
            disabled={saving}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r] ?? r}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="contrib-work" className="input-label">
            Work description
          </label>
          <textarea
            id="contrib-work"
            value={workDescription}
            onChange={(e) => setWorkDescription(e.target.value)}
            maxLength={400}
            rows={3}
            className="input resize-none"
            placeholder="What did they do on this build?"
            disabled={saving}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contrib-date-from" className="input-label">
              Work started
            </label>
            <input
              type="date"
              id="contrib-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input min-h-[44px]"
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="contrib-date-to" className="input-label">
              Work finished
            </label>
            <input
              type="date"
              id="contrib-date-to"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                if (errors.date_to) setErrors((prev) => ({ ...prev, date_to: '' }));
              }}
              className={`input min-h-[44px] ${errors.date_to ? 'border-red-500' : ''}`}
              disabled={saving}
              aria-invalid={Boolean(errors.date_to)}
              aria-describedby={errors.date_to ? 'contrib-date-to-error' : undefined}
            />
            {errors.date_to && (
              <p id="contrib-date-to-error" className="mt-1 text-sm text-red-400">
                {errors.date_to}
              </p>
            )}
          </div>
        </div>
      </form>
    </EditorChrome>
    <ShopRequestModal
      isOpen={shopRequestOpen}
      onClose={() => setShopRequestOpen(false)}
      initialName={shopQuery.trim()}
    />
  </>
  );
};

export default ContributorRequestModal;
